import { PlanFamily } from 'src/types';
import {
  useGetDisplayedPlan,
  usePaymentControlState,
  useSubscriptionInfo,
} from 'src/hooks';
import { useMemo } from 'react';
import { PlanCardContent } from 'src/pages/PaymentBetaPage/components/PlanCardContent';
import { BuyWithAWSButton } from '../BuyWithAWSButton';
import { SelectedPlanButton } from '../SelectedPlanButton';
import { GetPlanButton } from '../GetPlanButton';
import { PlanCardTitle } from '../PlanCardTitle';
import { PlanCardPrice } from '../PlanCardPrice';
import { PlanCardDescription } from '../PlanCardDescription';
import styles from './PlanCard.module.scss';
import { PlanKindSelector } from '../PlanKindSelector';
import { PlanGroup } from 'src/types/wallet/models/PlanGroup';
import { NEW_PLANS_VARIANTS } from 'src/constants';

type PlanCardProps = {
  onOpenCheckout?: () => void;
  planFamily: PlanFamily;
};

export const PlanCard = ({ onOpenCheckout, planFamily }: PlanCardProps) => {
  const { selectedPlanSetByFamily, selectedFamily } = usePaymentControlState();

  const { getDisplayedPlan } = useGetDisplayedPlan();
  const displayedPlan = getDisplayedPlan(planFamily);

  const variant = displayedPlan?.variant;
  const isNewVariant =
    NEW_PLANS_VARIANTS.find((v) => v === variant) !== undefined;

  const planKind = selectedPlanSetByFamily[planFamily];
  const isSelected = selectedFamily === planFamily;

  const { subscriptionInfo } = useSubscriptionInfo();

  const isCurrentPlan = useMemo(() => {
    return subscriptionInfo?.plan_kind === planKind;
  }, [subscriptionInfo, planKind]);

  const subscribedTier = useMemo(() => {
    return subscriptionInfo?.plan_tier;
  }, [subscriptionInfo]);

  const isSubscribedTier = !!subscribedTier;

  const isDisabledPlanKindSelector = useMemo(() => {
    return (
      (subscriptionInfo?.plan_family === PlanFamily.BUSINESS ||
        subscriptionInfo?.plan_group === PlanGroup.TEAMS) &&
      planFamily !== PlanFamily.BUSINESS
    );
  }, [subscriptionInfo, planFamily]);

  // const isTeamsTier = useMemo(() => {
  //   return (
  //     subscribedTier === NinjaSubscriptionPlan.TEAMS_STANDARD ||
  //     subscribedTier === NinjaSubscriptionPlan.TEAMS_PRO ||
  //     subscribedTier === NinjaSubscriptionPlan.TEAMS_ULTRA
  //   );
  // }, [subscribedTier]);

  const onCheckoutClick = () => {
    onOpenCheckout?.();
  };

  // TODO: WIP. restore after MVP
  // const showBuyWithAws = planFamily === PlanFamily.BUSINESS && !isTeamsTier;
  const showBuyWithAws = false;

  return (
    <div className={styles.root}>
      <div className={styles.mainContainer}>
        <div className={styles.headerWrapper}>
          <PlanCardTitle planFamily={planFamily} />

          <PlanCardPrice planFamily={planFamily} />

          {!isNewVariant && (
            <PlanCardDescription
              planFamily={planFamily}
              isNewVariant={isNewVariant}
            />
          )}
        </div>

        <PlanKindSelector
          planFamily={planFamily}
          isDisabledPlanKindSelector={isDisabledPlanKindSelector}
          isNewVariant={isNewVariant}
        />

        <div className={styles.buttonWrapper}>
          {isSelected ? (
            <SelectedPlanButton />
          ) : (
            <GetPlanButton
              isDisabledPlanKindSelector={isDisabledPlanKindSelector}
              isCurrentPlan={isCurrentPlan}
              isSubscribedTier={isSubscribedTier}
              planFamily={planFamily}
              onCheckoutClick={onCheckoutClick}
            />
          )}

          {showBuyWithAws && (
            <BuyWithAWSButton isCurrentPlan={!!isCurrentPlan} />
          )}
        </div>

        {isNewVariant && (
          <PlanCardDescription
            planFamily={planFamily}
            isNewVariant={isNewVariant}
          />
        )}

        <PlanCardContent
          planFamily={planFamily}
          planKind={planKind}
          isNewVariant={isNewVariant}
        />
      </div>

      <p className={styles.footerCaption}>
        Cancel anytime, effective next billing cycle
      </p>
    </div>
  );
};
